import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPieChart, FiBarChart3 } = FiIcons;

const ChannelAnalysis = ({ selectedProduct }) => {
  // Mock channel data
  const superChannelData = [
    { channel: 'RETAIL', share: 54.86, color: '#3b82f6' },
    { channel: 'NON RETAIL', share: 35.04, color: '#10b981' },
    { channel: 'MAIL', share: 10.10, color: '#f59e0b' }
  ];

  const detailedChannelData = [
    { channel: 'MAIL', share: 53.78, color: '#8b5cf6' },
    { channel: 'NON RETAIL', share: 29.65, color: '#10b981' },
    { channel: 'RETAIL', share: 6.72, color: '#3b82f6' },
    { channel: 'INDEPENDENT', share: 4.33, color: '#ef4444' },
    { channel: 'CLINIC', share: 2.89, color: '#f59e0b' },
    { channel: 'FOOD STORES', share: 1.28, color: '#06b6d4' },
    { channel: 'LONG-TERM CARE', share: 0.85, color: '#84cc16' },
    { channel: 'GOVERNMENT HOSPITAL', share: 0.32, color: '#f97316' },
    { channel: 'FEDERAL FACILITIES', share: 0.18, color: '#ec4899' }
  ];

  // Mock market share by channel data
  const channelMarketShare = [
    {
      superChannel: 'RETAIL',
      channel: 'RETAIL',
      eaches: 20479,
      nspEaches: 52796,
      units: 20479,
      nspExtUnits: 52796,
      marketShare: 7.07,
      totalUnits: 309160,
      nspUnits: 798540
    },
    {
      superChannel: 'NON RETAIL',
      channel: 'NON RETAIL',
      eaches: 325295,
      nspEaches: 693400,
      units: 325295,
      nspExtUnits: 693400,
      marketShare: 92.78,
      totalUnits: 350540,
      nspUnits: 747460
    },
    {
      superChannel: 'MAIL',
      channel: 'MAIL',
      eaches: 9854,
      nspEaches: 21490,
      units: 9854,
      nspExtUnits: 21490,
      marketShare: 0.15,
      totalUnits: 6580340,
      nspUnits: 14365740
    }
  ];

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="space-y-6">
      {/* Channel Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Super Channel Distribution */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiPieChart} className="text-blue-600" />
            Super Channel (Last 12 Months)
          </h3>
          
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                {superChannelData.reduce((acc, item, index) => {
                  const startAngle = acc.currentAngle;
                  const endAngle = startAngle + (item.share / 100) * 360;
                  const largeArcFlag = item.share > 50 ? 1 : 0;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  const pathData = [
                    `M 50 50`,
                    `L ${x1} ${y1}`,
                    `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    `Z`
                  ].join(' ');
                  
                  acc.paths.push(
                    <path
                      key={index}
                      d={pathData}
                      fill={item.color}
                      className="hover:opacity-80 transition-opacity"
                    />
                  );
                  
                  acc.currentAngle = endAngle;
                  return acc;
                }, { paths: [], currentAngle: 0 }).paths}
              </svg>
            </div>
          </div>
          
          <div className="space-y-3">
            {superChannelData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="font-medium text-gray-900">{item.channel}</span>
                </div>
                <span className="font-semibold text-gray-900">{item.share}%</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Detailed Channel Breakdown */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiBarChart3} className="text-green-600" />
            Channels Drilldown (Last 12 Months)
          </h3>
          
          <div className="space-y-3">
            {detailedChannelData.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 text-sm truncate">{item.channel}</span>
                    <span className="font-semibold text-gray-900 text-sm ml-2">{item.share}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.share}%` }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></motion.div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Channel Market Share Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Market Share by Channel - {selectedProduct}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Super Channel</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Channel</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Eaches</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">% (Eaches)</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Units</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">NSP Ext Units</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Market Share %</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Total Market Units</th>
              </tr>
            </thead>
            <tbody>
              {channelMarketShare.map((row, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6 font-medium text-gray-900">{row.superChannel}</td>
                  <td className="py-4 px-6 text-gray-700">{row.channel}</td>
                  <td className="py-4 px-6 text-right">{formatNumber(row.eaches)}</td>
                  <td className="py-4 px-6 text-right text-gray-700">
                    {((row.eaches / 355628) * 100).toFixed(2)}%
                  </td>
                  <td className="py-4 px-6 text-right">{formatNumber(row.units)}</td>
                  <td className="py-4 px-6 text-right">{formatNumber(row.nspExtUnits)}</td>
                  <td className="py-4 px-6 text-right font-medium text-blue-600">{row.marketShare}%</td>
                  <td className="py-4 px-6 text-right">{formatNumber(row.totalUnits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Channel Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiPieChart} className="text-blue-600 text-xl" />
            <h4 className="font-semibold text-blue-900">Retail Dominance</h4>
          </div>
          <p className="text-2xl font-bold text-blue-900 mb-2">54.86%</p>
          <p className="text-sm text-blue-700">
            Retail channel represents majority of market volume
          </p>
        </div>

        <div className="bg-green-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiBarChart3} className="text-green-600 text-xl" />
            <h4 className="font-semibold text-green-900">Non-Retail Strong</h4>
          </div>
          <p className="text-2xl font-bold text-green-900 mb-2">35.04%</p>
          <p className="text-sm text-green-700">
            Significant non-retail market opportunity
          </p>
        </div>

        <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiPieChart} className="text-orange-600 text-xl" />
            <h4 className="font-semibold text-orange-900">Mail Order</h4>
          </div>
          <p className="text-2xl font-bold text-orange-900 mb-2">10.10%</p>
          <p className="text-sm text-orange-700">
            Growing mail order segment
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ChannelAnalysis;